import { useEffect } from "react";
import { getEnvironmentStatus } from "@/lib/api";
import { useChatStore } from "@/stores/useChatStore";

/**
 * 環境コンテナの起動状態を取得してストアへ反映する。取得中に別チャット・別環境へ
 * 切り替わっていた場合は setEnvironmentStatus 側で捨てられる。失敗はログのみで前回値を
 * 維持する (未取得のままなら起動中扱いになり、サーバー側の 409 に委ねる)。
 */
export async function syncChatEnvironmentStatus(
	environmentId: string,
	getStatus: typeof getEnvironmentStatus = getEnvironmentStatus,
): Promise<void> {
	try {
		const entry = await getStatus(environmentId);
		useChatStore
			.getState()
			.setEnvironmentStatus(environmentId, entry.status, entry.scheduledRestartAt ?? null);
	} catch (err: unknown) {
		console.error("syncChatEnvironmentStatus failed", err);
	}
}

/** 送信が 409 で弾かれた直後など、次のポーリングを待たずに現在チャットの環境状態を取り直す。 */
export function refreshChatEnvironmentStatus(): void {
	const { chatId, environmentId } = useChatStore.getState();
	if (chatId === null || environmentId === null) return;
	void syncChatEnvironmentStatus(environmentId);
}

/**
 * 現在チャットの環境の起動状態をストアと同期する。WorkspacePanels から一度だけマウントする。
 * 状態取得は環境 Pod の全件列挙を伴うため定期ポーリングはせず、チャット切替時の 1 回に
 * 留める。以降の変化は送信時 409 (refreshChatEnvironmentStatus) とプロジェクトページでの
 * 起動・停止操作 (setEnvironmentStatus) で追従する。
 */
export function useChatEnvironmentStatusSync(): void {
	const chatId = useChatStore((s) => s.chatId);
	const environmentId = useChatStore((s) => s.environmentId);

	useEffect(() => {
		if (chatId === null || environmentId === null) return;
		void syncChatEnvironmentStatus(environmentId);
	}, [chatId, environmentId]);
}
